import { PlayerName as DisplayedName } from './PlayerName';
import { getTeamPlayerIds, getPlayerKMultiplier, formatBoost, EngineSettings } from '../../engine';
import { useT } from '../../i18n';

export interface PlayerMatchRowProps {
  /** The match to render (Padel24/Americano slot-format, or a KM/seriespill pair-match when kmPairs is set). */
  match: any;
  /** The profile player whose perspective this row is rendered from (highlight + win/loss tag). */
  playerId: any;
  /** Player list used to resolve names/ELO for the slots. */
  players: any[];
  /** When set, normalises a KM pair-based match (team.pairId + score.{t1,t2}) into the slot format. */
  kmPairs?: any[] | null;
}

/**
 * One match row in a player profile — context label, both teams with per-player
 * and per-team ELO, score, and a win/loss/draw tag from the player's perspective.
 * Handles Padel24/Americano slot-format and normalises KM/seriespill pair-matches.
 * Extracted verbatim from the original artifact (legacy/App.jsx); markup/logic unchanged.
 *
 * Engine swaps (live Padel24Engine.elo.* → named engine imports, mirroring
 * UniversalMatchRow): getPlayerKMultiplier, formatBoost, EngineSettings.NEWBIE_MATCH_THRESHOLD.
 */
export function PlayerMatchRow({
  match: rawMatch,
  playerId,
  players,
  kmPairs = null,
}: PlayerMatchRowProps) {
  const t = useT();
  // KM matches use a pair structure (team.pairId) + score.{t1,t2}. Normalise to
  // the same form as Padel24/Americano so the rest of the row works identically.
  let m = rawMatch;
  let players2 = players;
  if (kmPairs && (rawMatch.team1?.pairId || rawMatch.team2?.pairId)) {
    const pairById = new Map(kmPairs.map((p) => [p.id, p]));
    const p1 = pairById.get(rawMatch.team1?.pairId);
    const p2 = pairById.get(rawMatch.team2?.pairId);
    const sc = rawMatch.score;
    m = {
      ...rawMatch,
      team1: { p1Id: p1?.p1Id, p2Id: p1?.p2Id },
      team2: { p1Id: p2?.p1Id, p2Id: p2?.p2Id },
      score1: sc ? sc.t1 : undefined,
      score2: sc ? sc.t2 : undefined,
      completed: !!sc,
      // ELO change from KM (team average) → same fields the Padel24 row reads
      teamElo1Pre: rawMatch.teamEloPre?.t1,
      teamElo2Pre: rawMatch.teamEloPre?.t2,
      eloChange1: rawMatch.teamEloChange?.t1,
      eloChange2: rawMatch.teamEloChange?.t2,
      // keep phase/round/isFinal/seasonLabel from ...rawMatch for the context label
    };
    // Player names from the pairs (KM stores names on the pair)
    players2 = [
      p1 && { id: p1.p1Id, name: p1.p1Name },
      p1 && { id: p1.p2Id, name: p1.p2Name },
      p2 && { id: p2.p1Id, name: p2.p1Name },
      p2 && { id: p2.p2Id, name: p2.p2Name },
      ...(players || []),
    ].filter(Boolean);
  }
  // Resolve players via getTeamPlayerIds — works for both formats
  // Normalised KM/league matches carry player names on the team — use them as a
  // name source so names show even if the player is not in the players list.
  {
    const embedded: any[] = [];
    [m.team1, m.team2].forEach((tm) => {
      if (tm?.p1Id && tm?.p1Name) embedded.push({ id: tm.p1Id, name: tm.p1Name });
      if (tm?.p2Id && tm?.p2Name) embedded.push({ id: tm.p2Id, name: tm.p2Name });
    });
    if (embedded.length) players2 = [...(players2 || []), ...embedded];
  }
  const [t1p1Id, t1p2Id] = getTeamPlayerIds(m.team1);
  const [t2p1Id, t2p2Id] = getTeamPlayerIds(m.team2);

  const inT1 = t1p1Id === playerId || t1p2Id === playerId;
  const myScore = m.score1;
  const oppScore = m.score2;
  const t1Won = m.completed && m.score1 > m.score2;
  const t2Won = m.completed && m.score2 > m.score1;

  const playerWon = (inT1 && t1Won) || (!inT1 && t2Won);
  const playerLost = (inT1 && t2Won) || (!inT1 && t1Won);
  const isDraw = m.completed && m.score1 === m.score2;
  const rowBg = playerWon ? 'bg-lime-bg/30' : playerLost ? 'bg-rose-soft/40' : '';

  // Compact context label (F=phase, R=round, S=season, B=court) so it fits.
  const fmtRound = (r) => {
    if (r == null) return null;
    if (typeof r === 'number') return `R${r}`;
    const map = {
      sf: t('players.matchRow.round.semi'),
      qf: t('players.matchRow.round.quarter'),
      r16: t('players.matchRow.round.r16'),
      r32: t('players.matchRow.round.r32'),
      final: t('players.matchRow.final'),
      f: t('players.matchRow.final'),
    };
    return map[String(r).toLowerCase()] || `R${r}`;
  };
  let phaseLabel = '';
  let roundLabel: any = null;
  if (m.isFinal) {
    phaseLabel = m.finalNumber
      ? t('players.matchRow.finalN', { number: String(m.finalNumber) })
      : t('players.matchRow.final');
  } else if (m.phase === 'finals') {
    phaseLabel = t('players.matchRow.final');
  } else if (m.phase === 'league') {
    phaseLabel = m.round != null ? `R${m.round}` : t('players.matchRow.league');
    if (m.seasonNum != null) roundLabel = `S${m.seasonNum}`;
    else if (m.seasonLabel) {
      const yr = String(m.seasonLabel).match(/(\d+)/);
      roundLabel = yr ? `S${yr[1]}` : 'S';
    }
  } else if (m.phase === 'group') {
    phaseLabel = t('players.matchRow.group');
    if (m.round != null) roundLabel = fmtRound(m.round);
  } else if (m.phase === 'knockout') {
    phaseLabel =
      m.round != null
        ? fmtRound(m.round) || t('players.matchRow.knockout')
        : t('players.matchRow.knockout');
  } else if (m.phase) {
    phaseLabel = `F${m.phase}`;
    if (m.round != null) roundLabel = fmtRound(m.round);
  } else if (m.round != null) {
    phaseLabel = fmtRound(m.round);
    if (m.court) roundLabel = `B${m.court}`;
  }

  // Team ELO: stored pre-match values, or compute from playerEloPre, or from current
  const t1p1Pre = m.playerEloPre?.[t1p1Id];
  const t1p2Pre = m.playerEloPre?.[t1p2Id];
  const t2p1Pre = m.playerEloPre?.[t2p1Id];
  const t2p2Pre = m.playerEloPre?.[t2p2Id];

  const playerByIdFn = (id) => (players2 || []).find((p) => p.id === id);

  const t1Avg =
    m.teamElo1Pre != null
      ? m.teamElo1Pre
      : t1p1Pre != null && t1p2Pre != null
        ? Math.round((t1p1Pre + t1p2Pre) / 2)
        : playerByIdFn(t1p1Id)?.elo != null && playerByIdFn(t1p2Id)?.elo != null
          ? Math.round((playerByIdFn(t1p1Id).elo + playerByIdFn(t1p2Id).elo) / 2)
          : null;
  const t2Avg =
    m.teamElo2Pre != null
      ? m.teamElo2Pre
      : t2p1Pre != null && t2p2Pre != null
        ? Math.round((t2p1Pre + t2p2Pre) / 2)
        : playerByIdFn(t2p1Id)?.elo != null && playerByIdFn(t2p2Id)?.elo != null
          ? Math.round((playerByIdFn(t2p1Id).elo + playerByIdFn(t2p2Id).elo) / 2)
          : null;

  const t1IsStronger = t1Avg != null && t2Avg != null && t1Avg > t2Avg;
  const t2IsStronger = t1Avg != null && t2Avg != null && t2Avg > t1Avg;

  // Per-team newbieMul from the match — used to compute the base ELO (without
  // boost) shown in the team pill. The actual change (with boost) is shown behind
  // the player's 2x pill.
  const t1MatchNewbieMul = m.t1NewbieMul ?? 1.0;
  const t2MatchNewbieMul = m.t2NewbieMul ?? 1.0;
  const t1UnboostedChange =
    m.eloChange1 != null && t1MatchNewbieMul > 0
      ? Math.round(m.eloChange1 / t1MatchNewbieMul)
      : m.eloChange1;
  const t2UnboostedChange =
    m.eloChange2 != null && t2MatchNewbieMul > 0
      ? Math.round(m.eloChange2 / t2MatchNewbieMul)
      : m.eloChange2;

  // Render one player line with ELO after the name.
  // teamChange = the team's actual ELO change (with boost). Shown behind the 2x pill.
  const PlayerName = ({ id, isWinner, teamChange }) => {
    if (!id) return <span className="text-xs text-ink-3 italic">—</span>;
    const p = playerByIdFn(id);
    if (!p) return <span className="text-xs text-ink-3 italic">—</span>;
    const isMe = id === playerId;
    const displayElo = m.playerEloPre?.[id] ?? p.elo;

    // Check whether the player is a new player (boosted) right now — full history
    // has no tournament context, so we use the default kMultiplier=1.0
    const newbieMul = getPlayerKMultiplier(p);
    const isBoosted = newbieMul > 1;

    return (
      <span
        className={`text-xs leading-tight flex items-center gap-1.5 ${isMe ? 'font-bold text-ink' : 'text-ink-2'} ${isWinner ? 'font-semibold' : ''}`}
      >
        <span className={`truncate ${p.isPlaceholder ? 'italic text-orange' : ''}`}>
          <DisplayedName player={p} />
        </span>
        {displayElo != null && !p.isPlaceholder && (
          <span
            className="font-mono text-[10px] text-ink-3 flex-shrink-0"
            title={
              m.playerEloPre?.[id] != null
                ? t('players.matchRow.eloBefore')
                : t('players.matchRow.eloCurrent')
            }
          >
            {displayElo}
          </span>
        )}
        {isBoosted && (
          <span
            className="font-mono text-[9px] font-bold px-1 rounded flex-shrink-0 inline-flex items-center gap-1"
            style={{ background: '#fef3c7', color: '#92400e' }}
            title={t('players.common.boostTitle', {
              boost: formatBoost(newbieMul),
              count: EngineSettings.NEWBIE_MATCH_THRESHOLD,
            })}
          >
            <span>{formatBoost(newbieMul)}</span>
            {m.completed && teamChange != null && (
              <span
                style={{
                  color: teamChange > 0 ? '#3f6212' : teamChange < 0 ? '#9f1239' : '#92400e',
                }}
              >
                {teamChange > 0 ? '+' : ''}
                {teamChange}
              </span>
            )}
          </span>
        )}
        {isMe && <span className="text-[9px] font-mono text-lime-deep flex-shrink-0">●</span>}
      </span>
    );
  };

  // Render the team-ELO cell: average on top, change below (same style as UniversalMatchRow).
  // The 2x badge is now shown behind the PLAYER NAME instead of here — gives better context
  // about who is actually a newbie. The team cell shows only average ELO and change.
  const renderTeamElo = (teamAvg, change, isStronger) => {
    const eloColor = isStronger ? 'text-lime-deep' : 'text-ink-3';
    return (
      <div className="text-center leading-tight flex-shrink-0" style={{ minWidth: 32 }}>
        {teamAvg != null ? (
          <div
            className={`font-mono text-[11px] font-semibold ${eloColor}`}
            title={t('players.matchRow.teamAvgTitle', {
              strongest: isStronger ? t('players.matchRow.strongestTeam') : '',
            })}
          >
            {teamAvg}
          </div>
        ) : (
          <div className="text-ink-3 text-[11px]">–</div>
        )}
        {m.completed && change != null && (
          <div
            className={`font-mono text-[11px] font-bold ${change > 0 ? 'text-lime-deep' : change < 0 ? 'text-rose' : 'text-ink-3'}`}
            title={t('players.matchRow.eloChangeTitle')}
          >
            {change > 0 ? '+' : ''}
            {change}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`px-3 py-2 rounded ${rowBg}`}>
      <div
        className="grid items-center gap-2"
        style={{ gridTemplateColumns: '70px 1fr auto 1fr 80px' }}
      >
        {/* Phase / context */}
        <div className="min-w-0">
          <div className="font-mono text-[10px] text-ink-3 uppercase tracking-wider leading-tight break-words">
            {phaseLabel}
            {roundLabel ? <span className="text-ink-4 ml-1">{roundLabel}</span> : null}
          </div>
          {m.time && (
            <div className="font-mono text-[10px] text-ink-3 whitespace-nowrap">{m.time}</div>
          )}
        </div>

        {/* Home team: players + team ELO on the right */}
        <div
          className="min-w-0 px-2 py-1 rounded"
          style={{ background: inT1 ? 'rgba(96, 165, 250, 0.12)' : 'transparent' }}
        >
          <div className="flex items-center gap-2">
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <PlayerName id={t1p1Id} isWinner={t1Won} teamChange={m.eloChange1} />
              <PlayerName id={t1p2Id} isWinner={t1Won} teamChange={m.eloChange1} />
            </div>
            {renderTeamElo(t1Avg, t1UnboostedChange, t1IsStronger)}
          </div>
        </div>

        {/* Score */}
        <div className="flex items-center justify-center gap-1.5 px-2">
          {m.completed ? (
            <>
              <span
                className={`font-mono text-base font-bold ${t1Won ? 'text-lime-deep' : 'text-ink-3'}`}
              >
                {myScore}
              </span>
              <span className="font-mono text-[10px] text-ink-4">–</span>
              <span
                className={`font-mono text-base font-bold ${t2Won ? 'text-lime-deep' : 'text-ink-3'}`}
              >
                {oppScore}
              </span>
            </>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 italic">
              vs
            </span>
          )}
        </div>

        {/* Away team: ELO on the left, then players (mirrored) */}
        <div
          className="min-w-0 px-2 py-1 rounded"
          style={{ background: !inT1 ? 'rgba(96, 165, 250, 0.12)' : 'transparent' }}
        >
          <div className="flex items-center gap-2">
            {renderTeamElo(t2Avg, t2UnboostedChange, t2IsStronger)}
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <PlayerName id={t2p1Id} isWinner={t2Won} teamChange={m.eloChange2} />
              <PlayerName id={t2p2Id} isWinner={t2Won} teamChange={m.eloChange2} />
            </div>
          </div>
        </div>

        {/* Result */}
        <div className="text-right">
          {!m.completed ? (
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 italic">
              {t('players.matchRow.notPlayed')}
            </span>
          ) : isDraw ? (
            <span className="inline-block px-2 py-0.5 rounded font-mono text-[10px] font-semibold uppercase tracking-wider bg-bg-soft text-ink-3">
              {t('players.matchRow.draw')}
            </span>
          ) : playerWon ? (
            <span className="inline-block px-2 py-0.5 rounded font-mono text-[10px] font-semibold uppercase tracking-wider bg-lime-bg text-lime-deep">
              {t('players.common.win')}
            </span>
          ) : (
            <span className="inline-block px-2 py-0.5 rounded font-mono text-[10px] font-semibold uppercase tracking-wider bg-rose-soft text-rose">
              {t('players.common.loss')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
